import { useState, useCallback, useMemo } from 'react';
import { Option } from '@/components/inputs/AutocompleteInput';
import type { RoutesFilter } from '../models';
import { RouteStatus } from '../enums';
import useRoutesSearchBarSelectedOptions from './useRoutesSearchBarSelectedOptions';
import useRoutesSearchRouteNameLiveFilter from './useRoutesSearchRouteNameLiveFilter';
import { useMonitoringIntegration } from '../context/monitoringIntegration.context';
import {
  applyRoutesFilterAutocompleteIdField,
  buildAppliedRoutesFilterFromLocal,
  driverChipOption,
  getScheduledDateRangeValidationMessage,
  optionDriverIdFromOption,
  toScheduledFromIsoFromPickerDate,
  toScheduledToIsoFromPickerDate,
} from '../utils';

interface UseRoutesSearchBarProps {
  initialFilter: RoutesFilter;
  filter: RoutesFilter;
  search: (appliedRoutesFilter: RoutesFilter) => void;
  mergeRoutesFilter: (partialRoutesFilter: Partial<RoutesFilter>) => void;
}

const useRoutesSearchBar = ({
  initialFilter,
  filter,
  search,
  mergeRoutesFilter,
}: UseRoutesSearchBarProps) => {
  const [localFilter, setLocalFilter] = useState<RoutesFilter>(() => ({
    ...filter,
  }));
  const [logisticOperatorInput, setLogisticOperatorInput] = useState('');
  const [driverInput, setDriverInput] = useState('');
  const [commerceInput, setCommerceInput] = useState('');
  const [dateRangeValidationMessage, setDateRangeValidationMessage] = useState<
    string | null
  >(null);

  const trimmedLogisticOperatorId =
    localFilter.logisticOperatorId?.trim() ?? '';
  const trimmedCommerceId = localFilter.commerceId?.trim() ?? '';

  const { selectedLogisticOperator, selectedCommerce } =
    useRoutesSearchBarSelectedOptions({
      logisticOperatorId: trimmedLogisticOperatorId,
      commerceId: trimmedCommerceId,
    });


  const { routeNameOptions, isRouteNamesLoading } =
    useRoutesSearchRouteNameLiveFilter({
      appliedRoutesFilter: filter,
      localRouteName: localFilter.routeName,
      mergeRoutesFilter,
    });

  const { useDriverOptions, useLogisticOperatorOptions, useCommerceOptions } =
    useMonitoringIntegration();

  const { logisticOperatorOptions, isLogisticOperatorsLoading } =
    useLogisticOperatorOptions({
      search: logisticOperatorInput,
      minLength: 3,
    });

  const { driverOptions, isDriversLoading } = useDriverOptions({
    search: driverInput,
    minLength: 3,
  });

  const { commerceOptions, isCommercesLoading } = useCommerceOptions({
    search: commerceInput,
    minLength: 3,
  });
  const driverAutocompleteOptions = useMemo(() => {
    const labels = localFilter.driverLabelsById ?? {};
    const byId = new Map<string, Option>();
    for (const id of localFilter.driverIds ?? []) {
      byId.set(id, driverChipOption(id, labels[id] ?? id));
    }
    for (const option of driverOptions) {
      const oid = optionDriverIdFromOption(option);
      if (oid) byId.set(oid, option);
    }
    return [...byId.values()];
  }, [localFilter.driverIds, localFilter.driverLabelsById, driverOptions]);

  const handleLogisticOperatorSelect = useCallback((option: Option | null) => {
    applyRoutesFilterAutocompleteIdField(
      option,
      setLocalFilter,
      'logisticOperatorId'
    );
  }, []);

  const handleDriverIdsChange = useCallback(
    (ids: string[]) => {
      setLocalFilter((previous) => {
        const prevLabels = previous.driverLabelsById ?? {};
        if (ids.length === 0) {
          return {
            ...previous,
            driverIds: undefined,
            driverLabelsById: undefined,
          };
        }
        const nextLabels: Record<string, string> = {};
        for (const id of ids) {
          const match = driverOptions.find(
            (o) => optionDriverIdFromOption(o) === id
          );
          nextLabels[id] = match?.label ?? prevLabels[id] ?? id;
        }
        return {
          ...previous,
          driverIds: ids,
          driverLabelsById: nextLabels,
        };
      });
    },
    [driverOptions]
  );

  const handleCommerceSelect = useCallback((option: Option | null) => {
    if (!option) {
      setLocalFilter((previous) => ({ ...previous, commerceId: undefined }));
      return;
    }
    const id =
      typeof option.data === 'string' ? option.data.trim() : '';
    setLocalFilter((previous) => ({
      ...previous,
      commerceId: id || undefined,
    }));
  }, []);

  const handleRouteNameInputChange = useCallback((value: string) => {
    setLocalFilter((previous) => ({
      ...previous,
      routeName: value.length > 0 ? value : undefined,
    }));
  }, []);

  const handleShippingNumberChange = useCallback((value: string | number) => {
    const text = String(value).trim();
    setLocalFilter((previous) => ({
      ...previous,
      shippingNumber: text.length > 0 ? text : undefined,
    }));
  }, []);

  const handleRouteIdChange = useCallback((value: string | number) => {
    const text = String(value).trim();
    setDateRangeValidationMessage(null);
    setLocalFilter((previous) => ({
      ...previous,
      routeId: text.length > 0 ? text : undefined,
    }));
  }, []);

  const handleRouteNameOptionSelect = useCallback((option: Option | null) => {
    if (option === null) {
      setLocalFilter((previous) => ({ ...previous, routeName: undefined }));
      return;
    }
    const optionData = option.data;
    const selectedRouteNameText =
      typeof optionData === 'string' && optionData.length > 0
        ? optionData
        : typeof option.label === 'string'
          ? option.label
          : '';
    if (selectedRouteNameText.length === 0) {
      setLocalFilter((previous) => ({ ...previous, routeName: undefined }));
      return;
    }
    setLocalFilter((previous) => ({
      ...previous,
      routeName: selectedRouteNameText,
    }));
  }, []);

  const handleSearch = useCallback(() => {
    const validationMessage =
      getScheduledDateRangeValidationMessage(localFilter);
    if (validationMessage) {
      setDateRangeValidationMessage(validationMessage);
      return;
    }

    setDateRangeValidationMessage(null);
    search(buildAppliedRoutesFilterFromLocal(localFilter));
  }, [localFilter, search]);

  const resetFilters = useCallback(() => {
    setLocalFilter(initialFilter);
    setLogisticOperatorInput('');
    setDriverInput('');
    setCommerceInput('');
    setDateRangeValidationMessage(null);
    search(initialFilter);
  }, [initialFilter, search]);

  const handleStatusChange = useCallback((value: string) => {
    setLocalFilter((previous) => ({
      ...previous,
      status: (value as RouteStatus) || undefined,
    }));
  }, []);

  const handleDateFromChange = useCallback((date: Date | null) => {
    setDateRangeValidationMessage(null);
    setLocalFilter((previous) => ({
      ...previous,
      scheduledFrom: date
        ? toScheduledFromIsoFromPickerDate(date)
        : undefined,
    }));
  }, []);

  const handleDateToChange = useCallback((date: Date | null) => {
    setDateRangeValidationMessage(null);
    setLocalFilter((previous) => ({
      ...previous,
      scheduledTo: date ? toScheduledToIsoFromPickerDate(date) : undefined,
    }));
  }, []);

  const routeNameInputValue = localFilter.routeName ?? '';

  return {
    localFilter,
    logisticOperatorInput,
    setLogisticOperatorInput,
    driverInput,
    setDriverInput,
    commerceInput,
    setCommerceInput,
    selectedLogisticOperator,
    selectedCommerce,
    logisticOperatorOptions,
    driverAutocompleteOptions,
    commerceOptions,
    isLogisticOperatorsLoading,
    isDriversLoading,
    isCommercesLoading,
    routeNameOptions,
    isRouteNamesLoading,
    routeNameInputValue,
    handleRouteNameInputChange,
    handleRouteNameOptionSelect,
    handleShippingNumberChange,
    handleRouteIdChange,
    handleLogisticOperatorSelect,
    handleCommerceSelect,
    handleDriverIdsChange,
    handleSearch,
    resetFilters,
    handleStatusChange,
    handleDateFromChange,
    handleDateToChange,
    dateRangeValidationMessage,
  };
};

export default useRoutesSearchBar;
